/**
 * billing-api — typed wrapper for the admin Billing & Payouts overview,
 * through the @v0y4ge/api-client `apiRaw` choke point (payments-api pattern).
 *
 * DECLARED op (`GET /v1/admin/payouts/overview`) served by the design-time mock
 * until the backend ships it. For Connect Standard the connected provider
 * (Stripe/PayPal/Adyen) owns the actual payout ledger + bank details — this
 * surface is a read-only summary + a deep-link into the provider dashboard, so
 * the FE never moves money or edits a payout here.
 */
import { apiRaw } from "@v0y4ge/api-client";
import { reportOpSource } from "../../shell/opSource";

const tag = <T,>(op: string, p: Promise<T>): Promise<T> =>
  p.then((r) => { reportOpSource(op, r); return r; });

export type PayoutStatus = "paid" | "in_transit" | "pending";
export type Provider = "stripe" | "paypal" | "adyen";

export interface Payout {
  id: string;
  provider?: Provider;    // which connected provider this payout came from
  amountMinor: number;
  currency: string;
  status: PayoutStatus;
  createdAt: string;      // ISO — initiated
  arrivalDate: string;    // ISO — expected/settled in the bank
  bankLast4: string;      // last-4 of the deposit account, or a label (e.g. "PayPal balance")
}

/** Per-provider payout summary — a tenant may have more than one live. */
export interface ProviderBalance {
  provider: Provider;
  availableMinor: number;
  pendingMinor: number;
  grossThisPeriodMinor: number;
  feesThisPeriodMinor: number;
  feeBps: number;                 // this provider's platform application fee (bps)
  dashboardUrl: string | null;    // deep-link into that provider's dashboard
  bankLast4: string;
}

export interface PayoutsOverview {
  /** Single connected provider, or null when more than one is live (see `providers`). */
  provider: Provider | null;
  providers: ProviderBalance[];   // one row per connected provider
  currency: string;
  availableMinor: number;         // aggregate ready to pay out (all providers)
  pendingMinor: number;           // aggregate in transit / not yet available
  nextPayout: { amountMinor: number; date: string } | null;
  feeBps: number;                 // representative platform application fee (basis points)
  grossThisPeriodMinor: number;   // aggregate
  feesThisPeriodMinor: number;    // aggregate
  dashboardUrl: string | null;    // primary provider dashboard (single-provider convenience)
  payouts: Payout[];
}

/** cap: billing.read · scope: org — balance, next payout, fee summary, history. */
export const getPayoutsOverview = () =>
  tag("getPayoutsOverview", apiRaw<PayoutsOverview>("/v1/admin/payouts/overview"));

export function formatMinor(amountMinor: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(amountMinor / 100);
  } catch {
    return `${(amountMinor / 100).toFixed(2)} ${currency}`;
  }
}
